// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { URL } from "../service/Service";
import {
  Bars3Icon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  ShoppingBagIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  BellIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import euformsLogo from "../images/euforms.png";
import { getRoleHome } from "./RoleRoute";
import { useI18n } from "../i18n/I18nContext";
import { STORE_ENABLED } from "../config/features";

export default function Header() {
  const [role, setRole] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [colorMode, setColorMode] = useState(
    () => localStorage.getItem("euforms-backoffice-theme") || "LIGHT",
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useI18n();

  useEffect(() => {
    document.documentElement.dataset.backofficeTheme = colorMode.toLowerCase();
    localStorage.setItem("euforms-backoffice-theme", colorMode);
    window.dispatchEvent(
      new CustomEvent("backoffice-theme-changed", { detail: colorMode }),
    );
  }, [colorMode]);

  useEffect(
    () => () => {
      delete document.documentElement.dataset.backofficeTheme;
    },
    [],
  );

  const toggleColorMode = () =>
    setColorMode((current) => (current === "DARK" ? "LIGHT" : "DARK"));

  useEffect(() => {
    const token = sessionStorage.getItem("jwt");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setRole(decoded?.authorities?.[0]?.authority || "");
      setUserEmail(decoded?.sub || "");
    } catch {
      setRole("");
      setUserEmail("");
    }
  }, []);

  const loadProfileImage = useCallback(async () => {
    const token = sessionStorage.getItem("jwt");
    if (!token) return;

    const response = await fetch(`${URL}/api/users/profile-picture`, {
      headers: {
        Accept: "image/*",
        Authorization: `Bearer ${token}`,
      },
      cache: "no-store",
    });

    if (response.status === 404 || response.status === 422) {
      setProfileImageUrl((currentUrl) => {
        if (currentUrl) globalThis.URL.revokeObjectURL(currentUrl);
        return "";
      });
      return;
    }
    if (!response.ok) return;

    const imageBlob = await response.blob();
    const nextUrl = globalThis.URL.createObjectURL(imageBlob);
    setProfileImageUrl((currentUrl) => {
      if (currentUrl) globalThis.URL.revokeObjectURL(currentUrl);
      return nextUrl;
    });
  }, []);

  const loadNotifications = useCallback(async () => {
    const token = sessionStorage.getItem("jwt");
    if (!token) return;
    try {
      const response = await fetch(`${URL}/api/support/tickets/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setNotifications(data.items || []);
      setUnreadNotifications(data.unreadCount || 0);
    } catch {
      // A navegação não deve falhar quando as notificações estão indisponíveis.
    }
  }, []);

  useEffect(() => {
    void loadProfileImage();
    window.addEventListener("profile-picture-updated", loadProfileImage);
    return () =>
      window.removeEventListener("profile-picture-updated", loadProfileImage);
  }, [loadProfileImage]);

  useEffect(() => {
    void loadNotifications();
    const refresh = window.setInterval(loadNotifications, 30000);
    window.addEventListener("support-notifications-updated", loadNotifications);
    return () => {
      window.clearInterval(refresh);
      window.removeEventListener(
        "support-notifications-updated",
        loadNotifications,
      );
    };
  }, [loadNotifications]);

  const markNotificationsRead = async () => {
    if (unreadNotifications === 0) return;
    const token = sessionStorage.getItem("jwt");
    await fetch(`${URL}/api/support/tickets/notifications/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    setUnreadNotifications(0);
  };

  const toggleNotifications = async () => {
    const willOpen = !notificationOpen;
    setNotificationOpen(willOpen);
    if (willOpen) await markNotificationsRead();
  };

  useEffect(
    () => () => {
      if (profileImageUrl) globalThis.URL.revokeObjectURL(profileImageUrl);
    },
    [profileImageUrl],
  );

  const isAdmin = role === "ADMIN";
  const isCompany = role === "COMPANY";
  const roleLabel =
    {
      ADMIN: "Administrador",
      COMPANY: "Empresa",
      USER: "Utilizador",
    }[role] || "";
  const links = isAdmin
    ? [
        { label: "Formulários", to: "/admin/dashboard", icon: DocumentTextIcon, activeWhen: ["/admin/dashboard", "/admin/forms"] },
        ...(STORE_ENABLED ? [{ label: "Loja", to: "/admin/store", icon: ShoppingBagIcon }] : []),
        { label: "Utilizadores", to: "/admin/users", icon: UsersIcon },
        { label: "Suporte", to: "/support", icon: LifebuoyIcon },
        {
          label: "A minha conta",
          to: "/admin/user",
          icon: UserCircleIcon,
          identifiesUser: true,
        },
      ]
    : isCompany
      ? [
          { label: "Formulários", to: "/company/dashboard", icon: DocumentTextIcon, activeWhen: ["/company/dashboard", "/forms"] },
          ...(STORE_ENABLED ? [{ label: "Loja", to: "/store", icon: ShoppingBagIcon }] : []),
          { label: "Equipa", to: "/company/users", icon: BuildingOffice2Icon },
          { label: "Ajuda", to: "/support", icon: LifebuoyIcon },
          {
            label: "A minha conta",
            to: "/user",
            icon: UserCircleIcon,
            identifiesUser: true,
          },
        ]
      : [
          { label: "Formulários", to: "/dashboard", icon: DocumentTextIcon, activeWhen: ["/dashboard", "/forms"] },
          ...(STORE_ENABLED ? [{ label: "Loja", to: "/store", icon: ShoppingBagIcon }] : []),
          { label: "Ajuda", to: "/support", icon: LifebuoyIcon },
          {
            label: "A minha conta",
            to: "/user",
            icon: UserCircleIcon,
            identifiesUser: true,
          },
        ];

  function logout() {
    if (profileImageUrl) globalThis.URL.revokeObjectURL(profileImageUrl);
    sessionStorage.removeItem("jwt");
    sessionStorage.removeItem("userRole");
    delete document.documentElement.dataset.backofficeTheme;
    navigate("/login");
  }

  return (
    <header className="modern-header">
      <div className="modern-header-inner">
        <NavLink to={getRoleHome(role)} className="modern-header-logo">
          <img src={euformsLogo} alt="Formulários" />
        </NavLink>

        <nav className="modern-header-nav" aria-label="Navegação principal">
          {links.map(({ label, to, icon: Icon, identifiesUser, children, activeWhen }) => children ? (
            <div key={label} className="group relative">
              <NavLink to={to} className={`modern-nav-link ${children.some((item) => location.pathname.startsWith(item.to)) ? "active" : ""}`}>
                <Icon />
                <span>{label}</span>
                <ChevronDownIcon className="!h-3.5 !w-3.5 transition group-hover:rotate-180" />
              </NavLink>
              <div className="invisible absolute left-0 top-full z-50 mt-2 w-56 translate-y-1 rounded-2xl border border-slate-200 bg-white p-2 text-slate-800 opacity-0 shadow-2xl transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                <p className="px-3 pb-2 pt-1 text-[10px] font-black uppercase tracking-[.18em] text-emerald-700">Gestão da loja</p>
                {children.map(({ label: childLabel, to: childTo, icon: ChildIcon }) => (
                  <NavLink key={childTo} to={childTo} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition hover:bg-emerald-50 hover:text-emerald-800 ${isActive ? "bg-emerald-50 text-emerald-800" : ""}`}>
                    <ChildIcon className="h-5 w-5" />{childLabel}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `modern-nav-link ${isActive || activeWhen?.some((path) => location.pathname.startsWith(path)) ? "active" : ""}`
              }
            >
              {identifiesUser && profileImageUrl ? (
                <img
                  className="modern-nav-avatar"
                  src={profileImageUrl}
                  alt=""
                />
              ) : (
                <Icon />
              )}
              <span className="modern-nav-link-copy">
                <span>{label}</span>
                {identifiesUser && (roleLabel || userEmail) && (
                  <small className="modern-nav-account-meta">
                    {roleLabel && <strong>{roleLabel}</strong>}
                    {userEmail && <span>{userEmail}</span>}
                  </small>
                )}
              </span>
            </NavLink>
          ))}
        </nav>

        <label className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 shadow-sm xl:flex">
          <span>Idioma</span>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className="rounded-lg border-0 bg-slate-50 py-1 pl-2 pr-7 text-xs font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500"
            aria-label="Idioma do backoffice"
          >
            <option value="PT">🇵🇹 PT</option>
            <option value="ES">🇪🇸 ES</option>
            <option value="EN">🇬🇧 EN</option>
          </select>
        </label>

        <div
          className="backoffice-theme-control hidden xl:flex"
          title="Alternar entre o modo claro e escuro"
        >
          <SunIcon aria-hidden="true" />
          <button
            type="button"
            role="switch"
            aria-checked={colorMode === "DARK"}
            aria-label="Usar modo escuro"
            className={`backoffice-theme-switch ${colorMode === "DARK" ? "is-dark" : ""}`}
            onClick={toggleColorMode}
          >
            <span />
          </button>
          <MoonIcon aria-hidden="true" />
          <InformationCircleIcon
            className="backoffice-theme-info"
            aria-hidden="true"
          />
        </div>

        <div className="relative hidden xl:block">
          <button
            type="button"
            onClick={toggleNotifications}
            className="relative grid h-10 w-10 place-items-center rounded-xl border border-white/20 text-white transition hover:bg-white/10"
            aria-label={`${unreadNotifications} notificações não lidas`}
            aria-expanded={notificationOpen}
          >
            <BellIcon className="h-5 w-5" />
            {unreadNotifications > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-black text-white">
                {unreadNotifications > 9 ? "9+" : unreadNotifications}
              </span>
            )}
          </button>
          {notificationOpen && (
            <div className="absolute right-0 top-12 z-50 w-96 overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <p className="font-black">Notificações</p>
                  <p className="text-xs text-slate-500">
                    Atualizações do suporte
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotificationOpen(false)}
                  className="rounded-lg p-1 hover:bg-slate-100"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.map((notification) => (
                  <button
                    type="button"
                    key={notification.id}
                    onClick={() => {
                      setNotificationOpen(false);
                      navigate("/support");
                    }}
                    className="block w-full border-b border-slate-100 px-5 py-4 text-left hover:bg-emerald-50/50"
                  >
                    <p className="text-sm font-bold">{notification.message}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Ticket #{notification.ticketId.slice(0, 8)} ·{" "}
                      {new Date(notification.createdAt).toLocaleString("pt-PT")}
                    </p>
                  </button>
                ))}
                {!notifications.length && (
                  <p className="px-5 py-10 text-center text-sm text-slate-500">
                    Sem novas notificações.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setNotificationOpen(false);
                  navigate("/support");
                }}
                className="w-full bg-slate-50 px-5 py-3 text-sm font-bold text-emerald-800"
              >
                Abrir suporte
              </button>
            </div>
          )}
        </div>

        <button type="button" className="modern-logout" onClick={logout}>
          <ArrowRightOnRectangleIcon /> <span>Sair</span>
        </button>
        <button
          type="button"
          className="modern-menu-button"
          onClick={() => setOpen(!open)}
          aria-label="Abrir menu"
          aria-expanded={open}
        >
          {open ? <XMarkIcon /> : <Bars3Icon />}
        </button>
      </div>

      {open && (
        <nav className="modern-mobile-nav" aria-label="Navegação móvel">
          <button
            type="button"
            className="modern-nav-link"
            onClick={async () => {
              await markNotificationsRead();
              setOpen(false);
              navigate("/support");
            }}
          >
            <span className="relative">
              <BellIcon />
              {unreadNotifications > 0 && (
                <span className="absolute -right-2 -top-2 rounded-full bg-red-500 px-1.5 text-[10px] font-black text-white">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
              )}
            </span>
            <span>Notificações</span>
          </button>
          {links.flatMap((item) => item.children ? [{ label: item.label, section: true }, ...item.children] : [item]).map(({ label, to, icon: Icon, identifiesUser, section, activeWhen }, index) => section ? (
            <p key={`${label}-${index}`} className="px-4 pb-1 pt-3 text-xs font-black uppercase tracking-[.18em] text-emerald-700">{label}</p>
          ) : (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `modern-nav-link ${isActive || activeWhen?.some((path) => location.pathname.startsWith(path)) ? "active" : ""}`
              }
            >
              {identifiesUser && profileImageUrl ? (
                <img
                  className="modern-nav-avatar"
                  src={profileImageUrl}
                  alt=""
                />
              ) : (
                <Icon />
              )}
              <span className="modern-nav-link-copy">
                <span>{label}</span>
                {identifiesUser && (roleLabel || userEmail) && (
                  <small className="modern-nav-account-meta">
                    {roleLabel && <strong>{roleLabel}</strong>}
                    {userEmail && <span>{userEmail}</span>}
                  </small>
                )}
              </span>
            </NavLink>
          ))}
          <label className="mx-2 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
            <span>Idioma</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-sm font-bold text-slate-900"
              aria-label="Idioma do backoffice"
            >
              <option value="PT">🇵🇹 PT</option>
              <option value="ES">🇪🇸 ES</option>
              <option value="EN">🇬🇧 EN</option>
            </select>
          </label>
          <button
            type="button"
            className="backoffice-theme-mobile"
            onClick={toggleColorMode}
          >
            {colorMode === "DARK" ? <MoonIcon /> : <SunIcon />}
            <span>{colorMode === "DARK" ? "Modo escuro" : "Modo claro"}</span>
            <span
              className={`backoffice-theme-switch ${colorMode === "DARK" ? "is-dark" : ""}`}
              aria-hidden="true"
            >
              <span />
            </span>
          </button>
          <button type="button" className="modern-nav-link" onClick={logout}>
            <ArrowRightOnRectangleIcon />
            <span>Sair</span>
          </button>
        </nav>
      )}
    </header>
  );
}
